import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { useObserver } from 'mobx-react-lite';

import useUI from 'src/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useConciergeTheme } from './use-concierge-theme';
import { useIsKioskConciergeChromeActive } from './use-is-kiosk-concierge-chrome-active';
import { resolveConciergeAccentHex, resolveConciergeChromeForeground } from './concierge-theme';

// The styled theme is untyped repo-wide (no @types/styled-components); this is
// the minimal structural slice the merge touches.
type ChromeTheme = Record<string, unknown> & {
  customized: Record<string, unknown> & { colors: Record<string, unknown> };
};

// The concierge scheme re-points --dct--customized-colors-buttonsLinks and
// --dcm-color-primary on <body>. This keeps kiosk chrome CSS-variable readers on
// the store accent. The remote
// container re-declares the concierge accent for its own subtree.
const KioskChromeAccentScope = createGlobalStyle`
  /* The [data-concierge-theme] variant outranks the scheme block's .concierge-mode[data-concierge-theme] selector. */
  body.concierge-mode,
  body.concierge-mode[data-concierge-theme] {
    --dct--customized-colors-buttonsLinks: ${({ theme }) => theme.customized.colors.buttonsLinks};
    /* Same for the core alias: resolves through the restored variable above. */
    --dcm-color-primary: var(--dct--customized-colors-buttonsLinks);
  }

  /* A custom property's var() resolves where it is declared, so this alias keeps the
     :root (store) text color after <body> re-points --dcm-color-text-primary. */
  :root {
    --dct-kiosk-chrome-text-primary: var(--dcm-color-text-primary);
  }

  /* Kiosk header text (for example the menu-type label) reads the store text color. */
  header[data-testid="dispensary-header"] {
    --dcm-color-text-primary: var(--dct-kiosk-chrome-text-primary);
  }
`;

type ChromeAccent = { accentHex: string; foregroundHex: string };

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Hands accent-filled kiosk chrome the concierge accent while the concierge is
 * active. JS theme reads cannot see the concierge-mode CSS cascade, so filled
 * surfaces opt in through buttonsLinksFill. Accent text/icons on white keep
 * reading the store buttonsLinks.
 *
 * Mounted at shell level so modal and drawer portals inherit. Off the kiosk
 * concierge route this only checks the route: the theme and gate hooks live in
 * ConciergeAccentResolver, which mounts on the route and publishes the accent
 * here. The provider stays mounted so a route flip never remounts the shell below it.
 */
export function ConciergeKioskChromeTheme({ children }: { children: React.ReactNode }): JSX.Element {
  const UI = useUI();
  const isOnRoute = useObserver(() => UI.isKiosk && UI.isConciergeActive);
  const [accent, setAccent] = useState<ChromeAccent | null>(null);
  const activeAccent = isOnRoute ? accent : null;

  const theme = useMemo(
    () => (outerTheme: ChromeTheme) =>
      activeAccent
        ? {
            ...outerTheme,
            customized: {
              ...outerTheme.customized,
              colors: {
                ...outerTheme.customized.colors,
                // buttonsLinks stays the store accent: accent text/icons on white
                // read it and must keep the store value. Only accent-filled surfaces
                // read buttonsLinksFill.
                buttonsLinksFill: activeAccent.accentHex,
                // Additive ink for accent-filled surfaces. Do not rename to
                // colors.white: that token also backs plain surfaces unrelated to
                // the accent in the same shared chrome components.
                buttonsLinksForeground: activeAccent.foregroundHex,
              },
            },
          }
        : outerTheme,
    [activeAccent]
  );

  return (
    <ThemeProvider theme={theme}>
      {isOnRoute && <ConciergeAccentResolver onResolve={setAccent} />}
      {activeAccent && <KioskChromeAccentScope />}
      {children}
    </ThemeProvider>
  );
}

// Runs the theme and gate hooks, so it mounts only on the kiosk concierge route.
function ConciergeAccentResolver({ onResolve }: { onResolve: (accent: ChromeAccent | null) => void }): null {
  const { dispensary } = useDispensary();
  const isActive = useIsKioskConciergeChromeActive(dispensary, true);
  const resolved = useConciergeTheme();
  const accentHex = resolveConciergeAccentHex(resolved.settings);
  const foregroundHex = resolveConciergeChromeForeground(accentHex);

  useIsomorphicLayoutEffect(() => {
    onResolve(isActive ? { accentHex, foregroundHex } : null);
    return () => onResolve(null);
  }, [isActive, accentHex, foregroundHex, onResolve]);

  return null;
}
